/**
 * Content for the homepage's LayeredExplainer (doc 23 §2-§3, §9).
 *
 * Content-as-data: the component renders whatever this file describes and
 * holds no copy of its own. Per-sport content is derived from sportsData.js
 * (the single source of truth for sport copy), so nothing here duplicates it.
 *
 * Tennis additionally gets the real worked examples from doc 23 §3 (real test
 * account data — keep verbatim, don't paraphrase) plus real app screenshots
 * in public/landing/shots/. The other five sports deliberately get no
 * screenshot and no example numbers: we have no real captures for them, and
 * nothing here should be invented.
 */
import { SPORTS } from './sportsData';

export const LAYERS = [
  {
    key: 'season', num: '01', title: 'Season', color: 'amber',
    overview: "Define the goals and milestones you're chasing over the coming months. Follow up and assess your progress and outcomes as the season unfolds.",
  },
  {
    key: 'competitions', num: '02', title: 'Competitions', color: 'purple',
    overview: "Set up the tournaments and leagues you're competing in. Set your goals going in, then reflect on how you performed.",
  },
  {
    key: 'gamePlans', num: '03', title: 'Game Plans', color: 'accent',
    overview: 'Prepare for every match with a plan and a goal. Afterward, reflect on your performance and build a growing record of your opponents and matches.',
  },
  {
    key: 'gameAnalytics', num: '04', title: 'Game Analytics', color: 'teal',
    overview: 'Review your performance across seasons, tournaments, leagues, opponents and intensity levels.',
  },
  {
    key: 'dataCoach', num: '05', title: 'Data Coach', color: 'green',
    overview: 'An analytical sparring partner that reads your data, spots patterns and recurring themes across your full match history. Data Coach can recognize valuable insights and offer tips and advice.',
  },
];

// The real Game Analytics sub-tabs (GamePlanView.jsx) — identical for every sport.
const ANALYTICS_VIEWS = [
  'Dashboard',
  'Analytics',
  'Heads Up',
  'Competitions',
  'Stats by Equipment',
  'Notes Library',
];

// crop/hl are source px in the 800×612 captures (used by the demo player's
// zoom-drill); caption is read straight off the visible screenshot content.
const SHOT = (file, label, alt, drill) => ({ src: `/landing/shots/${file}`, label, alt, ...drill });

// Real tennis data, verbatim from doc 23 §3.
const TENNIS = {
  season: {
    example: {
      title: 'Autumn 2026',
      lines: [
        { k: 'Goal', v: 'Win 4 tennis matches in Division 5' },
        { k: 'Skill', v: 'Hold serve consistency above 70%', tag: 'active' },
        { k: 'Result', v: 'Reach quarterfinal, club championship', tag: 'active' },
      ],
    },
    shot: SHOT('season.jpg', 'Season', 'ZenAnalytics season view for a tennis player',
      { crop: [20, 345, 750, 165], hl: [[30, 385, 720, 55]], caption: 'Fall 2026. The goal: win 4 tennis matches in division 5.' }),
  },
  competitions: {
    example: {
      title: 'Kirtecup Lohko 2 syksy 2026 · league · 5 matches planned',
      lines: [
        { k: 'Goal', v: 'Win league, promotion to league 1' },
        { k: 'Plan', v: "One key element is patience. We can't keep hitting too aggressive. We need to be patient, hit deep, get more timing and precision to our attacks. Serve has improved, let's use it to the maximum by holding our serves. Backhand improvement is key training theme." },
      ],
    },
    example2: {
      title: 'KirteCup Syksy 2026 · archived · Goal achieved ✓',
      lines: [
        { k: 'Review', v: 'Performance ★★★★☆ · Plan outcome ★★★☆☆ · Tactical execution ★★★★☆ · Mental focus ★★★☆☆' },
      ],
    },
    shot: SHOT('compform.jpg', 'Competition', 'ZenAnalytics competition form for a tennis league',
      { crop: [85, 40, 590, 245], hl: [[95, 215, 450, 58]], caption: 'League, tournament or practice.' }),
  },
  gamePlans: {
    example: {
      title: 'vs. Tuomas · Oct 19, 2026 · KirteCup Syksy 2026, Round 3',
      lines: [
        { k: 'Goal', v: 'Hold serve, stay patient in rallies' },
        { k: 'Plan', v: 'Avoid going to the net — Tuomas wins passing shots there. Play from the baseline, put pressure on his backhand. That\'s where he cracks.' },
      ],
    },
    example2: {
      title: 'Loss 1-6, 1-6 · Performance 2★ · Plan adherence 4★',
      lines: [
        { k: 'Strokes', v: 'Serve 4★ · Return 3★ · Forehand 2★ · Backhand 2★ · Net play 1★' },
        { k: 'Notes', v: '"I went to the net about 20 times and won 1 or 2 points. When I backed off and only played from the back, I started to succeed."' },
      ],
    },
    shot: SHOT('gp1.jpg', 'Game analytics', 'A tennis game plan with its goal and plan, as stored in ZenAnalytics',
      { crop: [30, 290, 730, 180], hl: [[40, 360, 700, 105]], caption: 'Open the court with the serve, finish with the forehand.' }),
  },
  gameAnalytics: {
    shot: SHOT('analytics.jpg', 'Analytics', 'ZenAnalytics analytics view for a tennis player',
      { crop: [40, 215, 720, 290], hl: [[45, 225, 570, 55]], caption: 'Serve, return, forehand, backhand and net play — rated after every match.' }),
  },
  dataCoach: {
    example: {
      title: 'Forehand Analysis: Last 10 Matches',
      q: 'Can you analyze my forehand from my last 10 matches, highlighting my development and what’s working well?',
      lines: [
        { k: 'Trend', v: 'Averaging 3.4★ over the last 10 matches (vs. 2.93★ over the previous 15) — 5 wins (50%), with 4★ ratings clustering around the wins.' },
        { k: 'Oct 6 (W)', v: '“Forehand down the line was working all match.”' },
        { k: 'Oct 9 (W)', v: '“Slice worked exactly as planned.”' },
        { k: 'Oct 27 (W)', v: '“Best serving match in weeks” — 4★ forehand, 6-2, 6-3.' },
      ],
    },
  },
};

/**
 * Returns the zoomed-state content for one layer and one sport:
 * { groups: [{ label, items }], example?, example2?, quote?, shot? }
 */
export function layerDetail(layerKey, sportValue) {
  const sport = SPORTS.find((s) => s.value === sportValue) || SPORTS[0];
  const real = sportValue === 'tennis' ? TENNIS[layerKey] || {} : {};
  const base = { example: real.example, example2: real.example2, shot: real.shot };

  switch (layerKey) {
    case 'season':
      return { ...base, lead: sport.tagline, groups: [{ label: 'Season', items: sport.stages[0].items }] };
    case 'competitions':
      return { ...base, groups: [{ label: 'Plan & review', items: sport.stages[1].items }] };
    case 'gamePlans':
      return {
        ...base,
        groups: [
          { label: 'Before', items: sport.matchCycle.before },
          { label: 'After', items: [sport.matchCycle.afterQualitative, ...sport.matchCycle.afterQuantitative] },
        ],
      };
    case 'gameAnalytics':
      return { ...base, chips: ANALYTICS_VIEWS };
    case 'dataCoach':
      return { ...base, quote: sportValue === 'tennis' ? null : sport.quote };
    default:
      return base;
  }
}

/* ─── Cinematic demo: beat list per sport ───────────────────────────────────
 * Timing is derived from content, never flat: each revealed line starts after
 * the previous one has had time to be read (REVEAL ms/word), and the beat then
 * holds long enough to finish reading (READ ms/word + HOLD).
 * Baseline: motion-calibration/index.html ran title cards ~1.7s and shot beats
 * 2.2–3.2s; this player is deliberately slower because it carries more text.
 */
const words = (s) => (s ? String(s).split(/\s+/).filter(Boolean).length : 0);
// ~20% faster than the original pacing pass, per feedback that the demo ran long.
const SPEED = 0.8;
const ms = (v) => Math.round(v * SPEED);
const ENTER = ms(700);   // beat enters before first line appears
const REVEAL = ms(210);  // ms per word before the next line is revealed
const READ = ms(260);    // ms per word of total reading budget
const HOLD = ms(1600);   // extra settle time once everything is visible
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

/** Reveal delays + duration for a list of text lines. */
export function paceLines(lines, { min = ms(4200), max = ms(15000), lead = 0 } = {}) {
  let t = ENTER + lead;
  const delays = lines.map((l) => { const d = t; t += 500 + words(l) * REVEAL; return d; });
  const total = lines.reduce((n, l) => n + words(l), 0);
  const dur = clamp(Math.max(t + HOLD, ENTER + lead + total * READ + HOLD), min, max);
  return { delays, dur };
}

// Title card: the layer "menu" (~1.4s), then number/name, then overview line.
export const TITLE_MENU_MS = ms(1400);
const titleDur = (layer) => clamp(TITLE_MENU_MS + ms(1500) + words(layer.overview) * ms(190), ms(4200), ms(8500));

export function demoBeats(sportValue) {
  const sport = SPORTS.find((s) => s.value === sportValue) || SPORTS[0];
  const tennis = sportValue === 'tennis';
  const beats = [{ kind: 'intro', layer: null, dur: ms(4200) }];

  LAYERS.forEach((layer, li) => {
    beats.push({ kind: 'title', layer: li, dur: titleDur(layer) });
    const d = layerDetail(layer.key, sportValue);
    const pts = (label, items, extra = {}) => {
      const p = paceLines(items, { lead: extra.intro ? 400 + words(extra.intro) * REVEAL : 0 });
      beats.push({ kind: 'points', layer: li, label, items, delays: p.delays, dur: p.dur, ...extra });
    };
    const ex = (e) => {
      if (!e) return;
      const lines = e.lines.map((l) => `${l.k} ${l.v}`);
      const p = paceLines(lines, { lead: 500 });
      beats.push({ kind: 'example', layer: li, ex: e, delays: p.delays, dur: p.dur });
    };
    const shot = () => {
      if (!d.shot || !d.shot.crop) return;
      beats.push({ kind: 'shot', layer: li, shot: d.shot, dur: clamp(ms(3600) + words(d.shot.caption) * READ, ms(5000), ms(8000)) });
    };

    switch (layer.key) {
      case 'season':
        if (tennis) { shot(); ex(d.example); } else pts('Season', d.groups[0].items, { intro: d.lead });
        break;
      case 'competitions':
        if (tennis) { shot(); ex(d.example); ex(d.example2); } else pts('Plan & review', d.groups[0].items);
        break;
      case 'gamePlans':
        if (tennis) { shot(); ex(d.example); ex(d.example2); } else {
          pts('Before the match', d.groups[0].items);
          pts('After the match', d.groups[1].items);
        }
        break;
      case 'gameAnalytics':
        if (tennis) shot();
        { const p = paceLines(d.chips.map(() => 'x x'), { min: ms(4200) });
          beats.push({ kind: 'chips', layer: li, items: d.chips, delays: p.delays, dur: p.dur }); }
        break;
      case 'dataCoach':
        if (tennis && d.example) {
          const e = d.example;
          const qMs = words(e.q) * ms(110);            // typing
          const answer = e.lines.map((l) => `${l.k} ${l.v}`);
          const start = ENTER + qMs + ms(2000);       // send + "reading your matches…"
          const p = paceLines(answer, { lead: start - ENTER, max: ms(26000) });
          beats.push({ kind: 'chat', layer: li, ex: e, qMs, answerAt: start, delays: p.delays, dur: p.dur });
        } else if (d.quote) {
          const p = paceLines([d.quote], { lead: 300 });
          beats.push({ kind: 'quote', layer: li, quote: d.quote, delays: p.delays, dur: p.dur });
        }
        break;
      default:
    }
  });
  beats.push({ kind: 'outro', layer: null, dur: ms(4200) });
  return beats.map((b, i) => ({ ...b, id: `${sport.value}-${i}` }));
}
